/**
 * TaskCardMobile — Sprint 291 (Tasks V2)
 *
 * Mobile presentation for a single admin task row.
 * Renders as a tappable card at `<lg` viewports. The desktop table stays
 * untouched at `lg+` (Tasks.jsx keeps its existing `<table>` and only
 * conditionally renders this component with `lg:hidden`).
 *
 * Contract:
 *   - Tapping anywhere on the card (except the checkbox) opens the edit
 *     form (via `onEdit(task)`).
 *   - The checkbox toggles pending↔completed via `onToggleStatus(task)`.
 *   - No delete affordance on the card; delete lives in the edit form.
 */

import React from 'react';
import { Phone, Mail, Repeat, Circle, User, Clock, AlertTriangle } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';

const TYPE_ICON = {
  call:        Phone,
  email:       Mail,
  'follow-up': Repeat,
  other:       Circle,
};

const PRIORITY_STYLES = {
  high:   { dot: 'bg-red-500',    text: 'text-red-700',    label: 'High' },
  medium: { dot: 'bg-amber-500',  text: 'text-amber-700',  label: 'Med'  },
  low:    { dot: 'bg-emerald-500',text: 'text-emerald-700',label: 'Low'  },
};

function formatDue(task) {
  if (!task.dueDate) return null;
  const d = new Date(task.dueTime ? `${task.dueDate}T${task.dueTime}:00` : `${task.dueDate}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  return d;
}

export default function TaskCardMobile({
  task,
  clientName,
  assigneeName,
  onEdit,
  onToggleStatus,
  now,
  startOfToday,
  isTechnician,
}) {
  const isCompleted = task.status === 'completed';
  const due = formatDue(task);
  const isOverdue = due && !isCompleted && due < startOfToday;
  const prio = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;
  const TypeIcon = TYPE_ICON[task.taskType] || Circle;

  const dueText = due
    ? due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      + (task.dueTime ? ` · ${task.dueTime}` : '')
    : null;

  const displayTitle = (task.title || '').trim() || '[Untitled]';

  return (
    <div
      className={`bg-white border rounded-lg p-3 shadow-sm active:scale-[0.99] transition-transform
        ${isOverdue ? 'border-red-200' : 'border-gray-200'}
        ${isCompleted ? 'opacity-60' : ''}`}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox — 44px tap target */}
        <div
          className="pt-0.5 -m-1 p-2"
          onClick={(e) => { e.stopPropagation(); onToggleStatus(task); }}
          data-testid={`task-card-checkbox-${task.id}`}
        >
          <Checkbox checked={isCompleted} onCheckedChange={() => onToggleStatus(task)} />
        </div>

        {/* Body — tapping opens edit */}
        <button
          type="button"
          onClick={() => !isTechnician && onEdit(task)}
          className="flex-1 min-w-0 text-left"
          data-testid={`task-card-body-${task.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <span className={`text-sm font-medium leading-snug ${isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
              {displayTitle}
            </span>
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold shrink-0 ${prio.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${prio.dot}`} />
              {prio.label}
            </span>
          </div>

          {task.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
              {task.description}
            </p>
          )}

          {/* Meta row 1: client + due */}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-600">
            <span className="inline-flex items-center gap-1 min-w-0">
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">
                {clientName || <span className="italic text-gray-400">Internal</span>}
              </span>
            </span>
            {dueText && (
              <span className={`inline-flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                {isOverdue
                  ? <AlertTriangle className="w-3 h-3 shrink-0" />
                  : <Clock className="w-3 h-3 shrink-0" />}
                {dueText}
              </span>
            )}
          </div>

          {/* Meta row 2: task type + assignee */}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1">
              <TypeIcon className="w-3 h-3 shrink-0" />
              {task.taskType || 'other'}
            </span>
            <span className="text-gray-400">·</span>
            <span className="truncate">{assigneeName || 'Unassigned'}</span>
          </div>
        </button>
      </div>
    </div>
  );
}
